import { Elements, PaymentMethodMessagingElement } from '@stripe/react-stripe-js';
import type { StripeElementsOptions, StripePaymentMethodMessagingElementOptions } from '@stripe/stripe-js';
import { useStripeContext } from 'context/StripeContext';
import { useEffect, useMemo, useState } from 'react';
import { isAffirmDisplayable } from '@util/affirm/eligibility';
import { AffirmTag } from './AffirmTag';

/**
 * The npm-bundled @stripe/stripe-js@1.46.0 types for the Payment Method
 * Messaging Element predate Stripe's 'affirm' support: the installed
 * .d.ts only types `paymentMethods: ('afterpay_clearpay' | 'klarna')[]`.
 * Stripe's live docs (docs.stripe.com/elements/payment-method-messaging,
 * verified 2026-07-13) confirm the real, currently-shipping option key
 * is `paymentMethodTypes` and DOES accept 'affirm' — the Element is
 * loaded dynamically from Stripe's CDN at runtime, so the actual
 * behavior is correct even though the local types lag behind.
 *
 * This type reflects the verified live API for this one call site. A
 * full @stripe/stripe-js bump (1.46 -> 9.x) touches every payment
 * surface in the app (checkout, seller payouts, offers, Apple/Google
 * Pay, address forms) and needs its own dedicated migration with full
 * regression — out of scope for this display-only PR.
 */
interface LivePaymentMethodMessagingOptions {
  amount: number;
  currency: 'USD';
  paymentMethodTypes: ['affirm'];
  countryCode?: 'US';
}

// Reserves vertical space for the element while it loads (avoids CLS)
// and during the pre-hydration window. Tailwind classes are mobile-first
// (unprefixed = base/mobile, sm: = desktop+): mobile commonly wraps to 2
// lines (~42px), desktop renders 1 line (~21-24px per Bill's render
// review of 96f48445) — reserve accordingly per breakpoint rather than a
// single value that either blank-gaps desktop or clips mobile.
const PLACEHOLDER_HEIGHT_CLASS = 'min-h-[42px] sm:min-h-[24px]';

// Brand-matched appearance for the Stripe-rendered element (design
// feedback: the default Stripe look didn't match the site). Colors/font
// mirror the locked brand theme (text #1B1B1B, General Sans — see
// RootLayout.tsx). spacingUnit widened from Stripe's 4px default to 6px
// per Bill's render review of 96f48445 — 2px read as a run-on sentence
// ("…interest-free with affirm See if you qualify"); 6px opens up the
// logo -> link gap instead of tightening it. Declared at module scope so
// its identity never changes across renders — passing a fresh object
// into `Elements options` on every render would remount the Element and
// reload the iframe.
const AFFIRM_ELEMENTS_APPEARANCE: NonNullable<StripeElementsOptions['appearance']> = {
  variables: {
    fontFamily: '"General Sans", system-ui, sans-serif',
    colorText: '#1B1B1B',
    spacingUnit: '6px',
  },
};

/**
 * General Sans is served locally (public/fonts/GeneralSans-Variable.ttf,
 * loaded app-wide via next/font/local in RootLayout.tsx) — there is no
 * hosted CSS URL to hand Stripe's `fonts: [{ cssSrc }]` option. Stripe's
 * Payment Method Messaging Element renders inside a cross-origin iframe,
 * so a relative path (or any @font-face already loaded into the parent
 * document) is invisible to it: the iframe needs its own absolute,
 * publicly-reachable URL to fetch the font file from. window.location.origin
 * is used instead of hardcoding the prod domain so this also renders
 * correctly on staging/preview deploys. Only ever called client-side
 * (this file's Elements provider only mounts post-hydration), so
 * `window` is always defined here.
 */
function getAffirmElementsFonts(): StripeElementsOptions['fonts'] {
  const origin = window.location.origin;
  return [
    {
      family: 'General Sans',
      src: `url(${origin}/fonts/GeneralSans-Variable.ttf) format('truetype')`,
      weight: '100 900',
    },
  ];
}

interface AffirmMessagingProps {
  /** Item price / subtotal+shipping / post-balance order total — dollars, not cents. Surface-local by design (see TASK-1372 §2 edge cases). */
  amountUSD: number;
  className?: string;
  /**
   * ISO 3166-1 alpha-2 shipping-country code (e.g. 'US', 'CA'), when the
   * caller actually knows it. Codex P2 / Bill's decision (GB #1372): the
   * element is hard-coded to US Affirm terms (see countryCode: 'US' in
   * AffirmMessagingElement below), so a listing/order a non-US buyer can't
   * actually get Affirm financing on must not advertise it. Checkout and the
   * cart drawer (CartSlideOutV3) know a signed-in buyer's saved country and
   * pass it here; PDP (ProductDetails) also passes it for signed-in viewers
   * (GB #1372 QA follow-up, fresh-eyes review on PR #1451) but stays
   * undefined for guests, since PDP has no visitor-country signal without an
   * account. The grid/carousel surfaces (ListProductCard, FeaturedListing)
   * use the lighter AffirmTag instead of this component and don't thread a
   * country at all — see the comments at those call sites. undefined/'US' =
   * current behavior; a normalized non-US code suppresses the element
   * entirely (not even the placeholder — there's nothing pending to reserve
   * space for). '' and whitespace-only values are treated as unknown, not
   * as a non-US country — see isAffirmDisplayable/normalizeCountryCode in
   * @util/affirm/eligibility.
   */
  buyerCountry?: string;
}

function AffirmMessagingElement({ amountUSD }: { amountUSD: number }) {
  const options: LivePaymentMethodMessagingOptions = {
    amount: Math.round(amountUSD * 100),
    currency: 'USD',
    paymentMethodTypes: ['affirm'],
    countryCode: 'US',
  };

  return (
    <PaymentMethodMessagingElement
      options={
        options as unknown as StripePaymentMethodMessagingElementOptions
      }
    />
  );
}

/**
 * Canonical Affirm financing messaging (GB #1372 Workstream A-D1).
 * Renders Stripe's provider-computed "as low as $X/mo" — zero local
 * math. Eligibility-gated; renders null entirely outside
 * AFFIRM_MIN-AFFIRM_MAX/AFFIRM_DISPLAY_MIN. Never blocks price/ATC
 * rendering — degrades to a static, numberless tag if Stripe can't
 * load, and to empty reserved space during SSR/pre-hydration.
 */
export function AffirmMessaging({ amountUSD, className, buyerCountry }: AffirmMessagingProps) {
  const { stripePromise, isLoading: stripeLoading, error: stripeError } =
    useStripeContext();
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Computed once (window.location.origin can't change within a session)
  // and memoized so the Elements provider's `options` object keeps a
  // stable identity across re-renders — a new object each render would
  // remount the Element and reload the Stripe iframe.
  const elementsOptions: StripeElementsOptions = useMemo(
    () => ({
      appearance: AFFIRM_ELEMENTS_APPEARANCE,
      fonts: hasMounted ? getAffirmElementsFonts() : undefined,
    }),
    [hasMounted]
  );

  // Combines the amount gate with the country gate (unknown/'US' only —
  // isAffirmDisplayable normalizes buyerCountry so a present-but-empty
  // value, e.g. userDoc.country's '' default for new users, can't slip
  // through as if it meant "unknown" and show US-only terms to a buyer
  // whose saved address says otherwise — GB #1372 QA follow-up, Codex P2).
  // Below the amount floor or for a known non-US buyer: render nothing,
  // not even the placeholder — there's no pending element to reserve
  // layout space for.
  if (!isAffirmDisplayable(amountUSD, buyerCountry)) return null;

  // SSR + pre-hydration: Stripe only exists client-side. Reserve space,
  // render nothing else to avoid a hydration mismatch.
  if (!hasMounted || stripeLoading) {
    return <div aria-hidden className={`${PLACEHOLDER_HEIGHT_CLASS} ${className ?? ''}`} />;
  }

  // Stripe blocked/unavailable (ad blocker, outage, bot-detection bail
  // in StripeContext) — numberless fallback. We never claim a $/mo
  // figure we can't back with the provider's own computation.
  if (stripeError || !stripePromise) {
    return (
      <div className={`${PLACEHOLDER_HEIGHT_CLASS} ${className ?? ''}`}>
        <AffirmTag amountUSD={amountUSD} />
      </div>
    );
  }

  return (
    <div className={`${PLACEHOLDER_HEIGHT_CLASS} ${className ?? ''}`}>
      <Elements stripe={stripePromise} options={elementsOptions}>
        <AffirmMessagingElement amountUSD={amountUSD} />
      </Elements>
    </div>
  );
}
